/**
 * Query + mutation hooks for Case Questions (#613, ADR-0150) — the firm↔firm
 * channel read through the CC worker's proxy routes.
 *
 *   useGlobalCaseQuestions()             → GET  /api/operations/case-questions
 *   useCaseQuestionsForLead(leadId, on)  → GET  /api/operations/case-questions/case/:leadId
 *   useAskCaseQuestion()                 → POST /api/operations/case-questions/ask
 *
 * Key space: ["case-questions", "global"] for the Firm↔Firm lane; ["case-
 * questions", "case", leadId] per matter. The ask mutation invalidates the whole
 * ["case-questions"] family — a new LF question belongs to some case key as well
 * as the global lane. The store is the portal's; an ask also mirrors to Slack
 * (portal-side — CC just calls the door).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CaseQuestionsResponseSchema,
  type AskCaseQuestionRequest,
  type AskCaseQuestionResponse,
  type CaseQuestionsResponse,
} from "@command-center/shared";
import { apiGet, apiPost } from "./api";
import { createPollingQuery } from "./create-polling-query";

const FAMILY_KEY = ["case-questions"] as const;
const GLOBAL_KEY = ["case-questions", "global"] as const;
const caseKey = (leadId: string) => ["case-questions", "case", leadId] as const;

/** Every question, the Firm↔Firm lane. Polls every 30s — the subtab count is a
 *  watch signal, and the fetch-all corpus is small (Messages-lane posture). */
export const useGlobalCaseQuestions = createPollingQuery({
  queryKey: GLOBAL_KEY,
  path: "/api/operations/case-questions",
  schema: CaseQuestionsResponseSchema,
  refetchIntervalMs: 30_000,
});

/** A matter's questions. Fetched only while the section is rendered
 *  (`enabled`) — the panel gates on `leadRecordId` being present. */
export function useCaseQuestionsForLead(leadId: string, enabled: boolean) {
  return useQuery<CaseQuestionsResponse, Error>({
    queryKey: caseKey(leadId),
    enabled,
    queryFn: async () => {
      const raw = await apiGet<unknown>(
        `/api/operations/case-questions/case/${encodeURIComponent(leadId)}`,
      );
      const parsed = CaseQuestionsResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/operations/case-questions/case/:leadId failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

/** LF co-asks a handling firm (the portal mirrors to Slack). Family-wide
 *  invalidation: the question belongs to a case key AND the global lane. */
export function useAskCaseQuestion() {
  const queryClient = useQueryClient();
  return useMutation<AskCaseQuestionResponse, Error, AskCaseQuestionRequest>({
    mutationFn: (req) =>
      apiPost<AskCaseQuestionResponse>("/api/operations/case-questions/ask", req),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
    },
  });
}
